import type { DesignTokens } from "../types/theme";

/**
 * Stockpilot brand stack — "Plus Jakarta Sans" for the UI (clean, geometric,
 * professional sans) with a system fallback. Quantities/values use tabular
 * numerals via `font-variant-numeric` in CSS, not here.
 */
const WEB_BRAND_FONT =
  "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * Base tokens for Light mode.
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f5f7fa",
  foreground: "#1e293b",
  surface: "#ffffff",
  surfaceForeground: "#1e293b",
  surfaceSecondary: "#eef2f7",
  surfaceSecondaryForeground: "#1e293b",
  surfaceTertiary: "#e2e8f0",
  surfaceTertiaryForeground: "#1e293b",
  overlay: "#ffffff",
  overlayForeground: "#1e293b",
  muted: "#64748b",
  default: "#eef2f7",
  defaultForeground: "#1e293b",
  accent: "#2563eb",
  accentForeground: "#ffffff",
  danger: "#dc2626",
  dangerForeground: "#ffffff",
  warning: "#f59e0b",
  warningForeground: "#1e293b",
  success: "#16a34a",
  successForeground: "#ffffff",
  separator: "#cbd5e1",
  glassBackground: "rgba(255, 255, 255, 0.85)",
  glassBorder: "rgba(30, 41, 59, 0.1)",
  glassHighlight: "rgba(30, 41, 59, 0.06)",
  glow0: "rgba(37, 99, 235, 0.14)",
  glow1: "rgba(67, 56, 202, 0.1)",
  glow2: "rgba(245, 158, 11, 0.1)",
  mutedForeground: "#1e293b",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(30, 41, 59, 0.1)",
  overlayShadow: "rgba(30, 41, 59, 0.22)",
  fieldShadow: "rgba(30, 41, 59, 0.08)",
  fieldBorder: "rgba(30, 41, 59, 0.18)",
  fieldPlaceholder: "rgba(30, 41, 59, 0.5)",
  fieldBackground: "rgba(30, 41, 59, 0.05)",
  segment: "#ffffff",
  segmentForeground: "#1e293b",
  link: "#2563eb",
  focus: "#2563eb",
  fieldForeground: "#1e293b",
  radius: "0.65rem",
  fontNormal: WEB_BRAND_FONT,
  fontMedium: WEB_BRAND_FONT,
  fontSemiBold: WEB_BRAND_FONT,
  fontBold: WEB_BRAND_FONT,
  border: "#cbd5e1",
  primary: "#2563eb",
  primaryForeground: "#ffffff",
  secondary: "#eef2f7",
  secondaryForeground: "#1e293b",
  backdrop: "rgba(15, 23, 42, 0.5)",
};

/**
 * Base tokens for Dark mode.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#0b1220",
  foreground: "#f1f5f9",
  surface: "#111c31",
  surfaceForeground: "#f1f5f9",
  surfaceSecondary: "#1a2640",
  surfaceSecondaryForeground: "#f1f5f9",
  surfaceTertiary: "#24324f",
  surfaceTertiaryForeground: "#f1f5f9",
  overlay: "#1a2640",
  overlayForeground: "#f1f5f9",
  muted: "#94a3b8",
  default: "#1f2c47",
  defaultForeground: "#f1f5f9",
  accent: "#3b82f6",
  accentForeground: "#ffffff",
  danger: "#f87171",
  dangerForeground: "#0b1220",
  warning: "#fbbf24",
  warningForeground: "#0b1220",
  success: "#22c55e",
  successForeground: "#0b1220",
  separator: "#33415c",
  glassBackground: "rgba(11, 18, 32, 0.985)",
  glassBorder: "rgba(203, 213, 225, 0.14)",
  glassHighlight: "rgba(203, 213, 225, 0.22)",
  glow0: "rgba(59, 130, 246, 0.45)",
  glow1: "rgba(67, 56, 202, 0.4)",
  glow2: "rgba(245, 158, 11, 0.3)",
  mutedForeground: "#f1f5f9",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.4)",
  overlayShadow: "rgba(0, 0, 0, 0.6)",
  fieldShadow: "rgba(0, 0, 0, 0.3)",
  fieldBorder: "rgba(203, 213, 225, 0.12)",
  fieldPlaceholder: "rgba(203, 213, 225, 0.42)",
  fieldBackground: "rgba(203, 213, 225, 0.05)",
  segment: "#24324f",
  segmentForeground: "#f1f5f9",
  link: "#3b82f6",
  focus: "#3b82f6",
  fieldForeground: "#f1f5f9",
  radius: "0.65rem",
  fontNormal: WEB_BRAND_FONT,
  fontMedium: WEB_BRAND_FONT,
  fontSemiBold: WEB_BRAND_FONT,
  fontBold: WEB_BRAND_FONT,
  border: "#33415c",
  primary: "#3b82f6",
  primaryForeground: "#ffffff",
  secondary: "#1f2c47",
  secondaryForeground: "#f1f5f9",
  backdrop: "rgba(2, 6, 16, 0.7)",
};
